import React, { useState, useEffect, useRef } from 'react';
import { Send, User, Sun, Plus, MessageSquare, Clock, Trash2, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TypewriterText = ({ text, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  
  useEffect(() => {
    let i = 0;
    setDisplayedText('');
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayedText(text.substring(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
        if (onComplete) onComplete();
      }
    }, 15);
    return () => clearInterval(timer);
  }, [text, onComplete]);

  return <>{displayedText}</>;
};

/* removed global WELCOME_MSG */
// eslint-disable-next-line no-unused-vars
const WELCOME_MSG = {
  id: 'welcome',
  sender: 'bot',
  text: 'Namaste, how can I assist you?',
  timestamp: new Date()
};

/* removed global QUICK_PROMPTS */
// eslint-disable-next-line no-unused-vars
const _DEL_QUICK = [
  { label: 'Yoga Ashram', value: 'Where is the best place to experience a traditional Yoga Ashram?' },
  { label: 'Ganga Aarti', value: 'How do I experience the sacred Ganga Aarti in Varanasi?' },
  { label: 'Ayurveda (Kerala)', value: 'Tell me about Ayurvedic healing and Panchakarma in Kerala.' },
  { label: 'Silent Meditation', value: 'I need absolute silence and mental clarity, where should I go?' }
];

// ── helpers ────────────────────────────────────────────────────────────────
function newSessionId() {
  const isAuth = localStorage.getItem('user_authenticated') === 'true' || localStorage.getItem('admin_authenticated') === 'true';
  const prefix = isAuth ? 'sess_reg_' : 'sess_gst_';
  return prefix + Math.random().toString(36).substring(2, 11);
}

function loadSessionsList() {
  try {
    const raw = localStorage.getItem('sadhaka_sessions');
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveSessionsList(list) {
  localStorage.setItem('sadhaka_sessions', JSON.stringify(list));
}

function formatRelativeTime(isoString, t) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return t('aiguide.justNow', { defaultValue: 'Just now' });
  if (mins < 60) return t('aiguide.minsAgo', { mins, defaultValue: `${mins}m ago` });
  if (hours < 24) return t('aiguide.hoursAgo', { hours, defaultValue: `${hours}h ago` });
  return t('aiguide.daysAgo', { days, defaultValue: `${days}d ago` });
}

// ── main component ─────────────────────────────────────────────────────────
export default function AIGuide() {
  const { t } = useTranslation();

  const WELCOME_MSG = {
    id: 'welcome',
    sender: 'bot',
    text: t('aiguide.welcome', { defaultValue: 'Namaste, how can I assist you?' }),
    timestamp: new Date()
  };

  const QUICK_PROMPTS = [
    { label: t('aiguide.q1Label', { defaultValue: 'Yoga Ashram' }), value: t('aiguide.q1Value', { defaultValue: 'Where is the best place to experience a traditional Yoga Ashram?' }) },
    { label: t('aiguide.q2Label', { defaultValue: 'Ganga Aarti' }), value: t('aiguide.q2Value', { defaultValue: 'How do I experience the sacred Ganga Aarti in Varanasi?' }) },
    { label: t('aiguide.q3Label', { defaultValue: 'Ayurveda (Kerala)' }), value: t('aiguide.q3Value', { defaultValue: 'Tell me about Ayurvedic healing and Panchakarma in Kerala.' }) },
    { label: t('aiguide.q4Label', { defaultValue: 'Silent Meditation' }), value: t('aiguide.q4Value', { defaultValue: 'I need absolute silence and mental clarity, where should I go?' }) }
  ];

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Session management
  const [sessions, setSessions] = useState([]);   // [{id, label, preview, updatedAt}]
  const [sessionId, setSessionId] = useState('');

  // Chat state
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const messagesContainerRef = useRef(null);

  // ── on mount: always start a fresh session ──────────────────────────────
  useEffect(() => {
    const freshId = newSessionId();
    const freshEntry = { id: freshId, label: t('aiguide.newJourney', { defaultValue: 'New Journey' }), preview: '', updatedAt: new Date().toISOString() };
    setSessions([freshEntry]);
    setSessionId(freshId);
    setMessages([WELCOME_MSG]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldScrollRef = useRef(false);

  // ── scroll to bottom on new messages ────────────
  useEffect(() => {
    if (shouldScrollRef.current && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({ top: messagesContainerRef.current.scrollHeight, behavior: 'smooth' });
      shouldScrollRef.current = false;
    }
  }, [messages, isTyping]);

  // ── load a session's history from backend ────────────────────────────────
  const loadSession = async (sessId, currentList) => {
    setSessionId(sessId);
    setMessages([WELCOME_MSG]);
    setLoadingHistory(true);
    try {
      const res = await fetch(`${apiBase}/api/ai/chat/${sessId}`);
      if (!res.ok) throw new Error('No history');
      const data = await res.json();
      if (data && data.length > 0) {
        setMessages(data.map(item => ({
          id: item.id,
          sender: item.sender,
          text: item.text,
          timestamp: new Date(item.timestamp)
        })));
      }
    } catch {
      setMessages([WELCOME_MSG]);
    } finally {
      setLoadingHistory(false);
    }

    // Mark as active in list
    const list = currentList || sessions;
    setSessions(list.map(s => s.id === sessId ? { ...s, active: true } : { ...s, active: false }));
  };

  // ── create a brand new chat session ─────────────────────────────────────
  const handleNewChat = () => {
    const id = newSessionId();
    const entry = { id, label: t('aiguide.newJourney', { defaultValue: 'New Journey' }), preview: '', updatedAt: new Date().toISOString() };
    const updated = [entry, ...sessions];
    setSessions(updated);
    saveSessionsList(updated);
    setSessionId(id);
    setMessages([WELCOME_MSG]);
    setInput('');
  };

  // ── switch to an existing session ────────────────────────────────────────
  const handleSelectSession = (sess) => {
    if (sess.id === sessionId) return;
    loadSession(sess.id, sessions);
  };

  // ── update session metadata after a message is sent ──────────────────────
  const updateSessionMeta = (sessId, preview) => {
    setSessions(prev => {
      const updated = prev.map(s =>
        s.id === sessId
          ? { ...s, preview: preview.substring(0, 50), updatedAt: new Date().toISOString(), label: s.label === t('aiguide.newJourney', { defaultValue: 'New Journey' }) ? preview.substring(0, 28) + '…' : s.label }
          : s
      );
      saveSessionsList(updated);
      return updated;
    });
  };

  // ── send message ─────────────────────────────────────────────────────────
  const handleSend = (textToSend) => {
    if (!textToSend.trim() || isTyping) return;

    const userMsg = { id: Date.now(), sender: 'user', text: textToSend, timestamp: new Date() };
    shouldScrollRef.current = true;
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    updateSessionMeta(sessionId, textToSend);

    fetch(`${apiBase}/api/ai/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, text: textToSend })
    })
      .then(res => { if (!res.ok) throw new Error('Backend error'); return res.json(); })
      .then(botMsg => {
        setTimeout(() => {
          shouldScrollRef.current = true;
          setMessages(prev => [...prev, {
            id: botMsg.id,
            sender: 'bot',
            text: botMsg.text,
            timestamp: new Date(botMsg.timestamp),
            isNew: true
          }]);
          setIsTyping(false);
          updateSessionMeta(sessionId, botMsg.text);
        }, 2000);
      })
      .catch(() => {
        setTimeout(() => {
          shouldScrollRef.current = true;
          setMessages(prev => [...prev, {
            id: Date.now() + 1,
            sender: 'bot',
            text: t('aiguide.errSignal', { defaultValue: 'The sacred signal is weak, seeker. Please check your connection and try again.' }),
            timestamp: new Date()
          }]);
          setIsTyping(false);
        }, 800);
      });
  };

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <section
      id="aiguide"
      style={{ padding: '30px 0 20px', backgroundColor: 'var(--bg-deep)', position: 'relative' }}
    >
      <div style={{ width: '95%', maxWidth: '850px', margin: '0 auto' }}>

        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
          <p className="font-serif" style={{ fontSize: '0.9rem', color: 'var(--primary-gold)', margin: '6px 0 0', letterSpacing: '0.05em' }}>
            {(() => {
              const hour = new Date().getHours();
              let greeting = 'Good evening';
              if (hour < 12) greeting = 'Good morning';
              else if (hour < 18) greeting = 'Good afternoon';

              const userName = sessionStorage.getItem('user_name') || sessionStorage.getItem('admin_name');
              return userName ? `${greeting}, ${userName}` : greeting;
            })()}
          </p>
          <p className="font-serif" style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '4px 0 0', letterSpacing: '0.05em' }}>
            Welcome to Mystic Routes
          </p>
          <div style={{ width: '30px', height: '2px', backgroundColor: 'var(--primary-gold)', margin: '10px auto 0' }} />
        </div>

        {/* Two-panel layout */}
        <div
          className="glass-card ai-guide-layout"
          style={{
            display: 'flex',
            height: 'calc(100vh - 140px)',
            minHeight: '500px',
            border: '1px solid rgba(181,137,37,0.2)',
            borderRadius: '24px',
            background: 'rgba(253, 251, 247, 0.75)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: '0 20px 50px rgba(41, 33, 26, 0.08)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* ── RIGHT PANEL: Chat Window ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

            {/* Top bar */}
            <div style={{
              padding: '15px 20px',
              borderBottom: '1px solid rgba(181, 137, 37, 0.2)',
              background: 'linear-gradient(135deg, rgba(20, 15, 10, 0.95) 0%, rgba(41, 33, 26, 0.95) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Glowing orb + name */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div className="orb-ripple" style={{ position: 'absolute', width: '20px', height: '20px', borderRadius: '50%', border: '1px solid var(--primary-gold)', opacity: 0, animation: 'ripple 3s infinite' }} />
                  <div
                    className="animate-breath"
                    style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'var(--primary-gold)', boxShadow: '0 0 10px var(--primary-gold)', animationDuration: '4s' }}
                  />
                </div>
                <div>
                  <span className="font-serif" style={{ fontSize: '0.9rem', color: 'rgba(255, 244, 233, 1)' }}>Darshan</span>
                  <span style={{ fontSize: '0.55rem', color: 'var(--text-muted)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('aiguide.advisor', { defaultValue: 'Meditative Travel Advisor' })}</span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className="hide-on-mobile" style={{ fontSize: '0.65rem', color: 'rgba(255, 244, 233, 1)', fontStyle: 'italic' }}>"{t('aiguide.quote', { defaultValue: 'Seek and you shall find' })}"</span>
              </div>
            </div>

            {/* Messages area */}
            <div
              ref={messagesContainerRef}
              style={{
                flex: 1,
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                background: 'transparent',
                overflowY: 'auto'
              }}
            >
              {loadingHistory ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.8rem', gap: '10px' }}>
                  <Sun size={14} className="animate-float" />
                  <span>{t('aiguide.loading', { defaultValue: 'Loading conversation…' })}</span>
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className="animate-slide-up"
                    style={{ display: 'flex', justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start', width: '100%' }}
                  >
                    <div style={{ display: 'flex', flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row', gap: '8px', maxWidth: '85%' }}>
                      {/* Avatar */}
                      <div style={{
                        width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
                        backgroundColor: msg.sender === 'user' ? 'var(--primary-saffron-trans)' : 'var(--primary-gold-trans)',
                        border: '1px solid',
                        borderColor: msg.sender === 'user' ? 'var(--primary-saffron-border)' : 'var(--primary-gold-border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: msg.sender === 'user' ? 'var(--primary-saffron)' : 'var(--primary-gold)'
                      }}>
                        {msg.sender === 'user' ? <User size={12} /> : <Sun size={12} />}
                      </div>

                      {/* Bubble */}
                      <div style={{
                        padding: '12px 16px',
                        borderRadius: '16px',
                        borderTopLeftRadius: msg.sender === 'bot' ? '4px' : '16px',
                        borderTopRightRadius: msg.sender === 'user' ? '4px' : '16px',
                        backgroundColor: msg.sender === 'user' ? 'rgba(194, 94, 26, 0.08)' : 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid',
                        borderColor: msg.sender === 'user' ? 'rgba(194, 94, 26, 0.25)' : 'rgba(181, 137, 37, 0.2)',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.03)',
                        fontSize: '0.88rem', lineHeight: '1.6', color: 'var(--text-light)'
                      }}>
                        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                          {msg.isNew && msg.sender === 'bot' ? (
                            <TypewriterText text={msg.text} onComplete={() => { msg.isNew = false; }} />
                          ) : (
                            msg.text
                          )}
                        </p>
                        <span style={{ display: 'block', fontSize: '0.6rem', color: 'var(--text-muted)', textAlign: 'right', marginTop: '4px' }}>
                          {msg.timestamp instanceof Date
                            ? msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}

              {/* Typing indicator */}
              {isTyping && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <div style={{ width: '30px', height: '30px', borderRadius: '50%', backgroundColor: 'var(--primary-gold-trans)', border: '1px solid var(--primary-gold-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary-gold)' }}>
                    <Sun size={14} className="animate-float" />
                  </div>
                  <div style={{ display: 'flex', gap: '4px', padding: '10px 14px', borderRadius: '12px', backgroundColor: 'rgba(255, 254, 254, 0.95)', border: '1px solid rgba(181,137,37,0.25)' }}>
                    {[0, 0.2, 0.4].map((delay, i) => (
                      <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--primary-gold)', animation: `bounce 1.4s ${delay}s infinite ease-in-out both` }} />
                    ))}
                  </div>
                </div>
              )}


            </div>

            {/* Quick prompts row */}
            {!isTyping && (
              <div style={{ position: 'sticky', bottom: '68px', zIndex: 4, display: 'flex', gap: '8px', padding: '10px 20px', overflowX: 'auto', borderTop: '1px solid rgba(181,137,37,0.1)', backgroundColor: 'rgba(253, 251, 247, 0.95)', scrollbarWidth: 'none' }} className="chips-scrollbar">
                {QUICK_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(p.value)}
                    style={{ padding: '6px 14px', borderRadius: '20px', backgroundColor: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.2)', color: 'var(--text-muted)', fontSize: '0.72rem', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'var(--transition-fast)' }}
                    className="chip-hover"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}

            {/* Input area */}
            <div style={{ padding: '15px 20px', borderTop: '1px solid rgba(181,137,37,0.2)', backgroundColor: 'rgba(253, 251, 247, 0.7)', backdropFilter: 'blur(10px)', display: 'flex', gap: '10px', flexShrink: 0, borderBottomLeftRadius: 'inherit', borderBottomRightRadius: 'inherit' }}>
              <input
                type="text"
                placeholder={t("aiguide.phAsk", { defaultValue: "Ask about sacred paths…" })}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSend(input); }}
                disabled={isTyping}
                style={{
                  flexGrow: 1, padding: '10px 15px',
                  background: 'rgba(255, 255, 255, 1)',
                  border: '1px solid rgba(0, 0, 0, 0.25)',
                  borderRadius: '30px', color: 'var(--text-light)',
                  outline: 'none', fontSize: '0.8rem',
                  opacity: isTyping ? 0.6 : 1
                }}
              />
              <button
                onClick={() => handleSend(input)}
                disabled={isTyping || !input.trim()}
                style={{
                  width: '38px', height: '38px', borderRadius: '50%',
                  backgroundColor: 'var(--primary-gold)', color: 'var(--text-dark)',
                  border: 'none', cursor: isTyping ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'var(--transition-fast)',
                  opacity: isTyping || !input.trim() ? 0.5 : 1
                }}
                className="send-button-hover"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1.0); }
        }
        @keyframes ripple {
          0% { transform: scale(0.5); opacity: 1; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        .chip-hover:hover {
          background-color: var(--primary-gold-trans) !important;
          color: var(--primary-gold) !important;
          border-color: var(--primary-gold) !important;
        }
        .send-button-hover:hover:not(:disabled) {
          background-color: var(--primary-gold-hover) !important;
          transform: scale(1.08);
        }
        .chips-scrollbar::-webkit-scrollbar { display: none; }
        .session-item:hover {
          background-color: rgba(181, 137, 37, 0.07) !important;
        }
        .new-chat-btn:hover {
          background-color: rgba(181, 137, 37, 0.18) !important;
          border-color: var(--primary-gold) !important;
        }
        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .ai-guide-layout {
            height: calc(100vh - 180px) !important;
            border-radius: 12px !important;
          }
          .hide-on-mobile {
            display: none !important;
          }
          #aiguide {
            padding: 10px 0 10px !important;
          }
        }
      `}</style>
    </section>
  );
}
